import { useEffect, useState } from "react";
import { BookOpen, ArrowLeft } from "@phosphor-icons/react";
import { educationApi } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function renderMarkdown(text) {
  // Renderizado mínimo: ##  = h3 · **texto** = bold · saltos de línea.
  const lines = (text || "").split("\n");
  const out = [];
  let paragraph = [];
  const flush = () => {
    if (paragraph.length === 0) return;
    out.push(
      <p key={`p-${out.length}`} className="text-sm leading-relaxed text-foreground/85 mb-4">
        {renderInline(paragraph.join(" "))}
      </p>
    );
    paragraph = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith("## ")) {
      flush();
      out.push(
        <h3 key={`h-${out.length}`} className="font-heading text-lg font-semibold text-primary mt-6 mb-2 tracking-tight">
          {l.slice(3).trim()}
        </h3>
      );
    } else if (l.trim() === "") {
      flush();
    } else {
      paragraph.push(l);
    }
  }
  flush();
  return out;
}

function renderInline(str) {
  // **bold** simple. Devuelve un array de nodos.
  const parts = str.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function EducacionPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    educationApi.list()
      .then(setArticles)
      .catch(() => toast.error("No se pudo cargar la biblioteca"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-muted-foreground">Cargando guías…</div>;

  if (selected) {
    return (
      <article className="max-w-3xl mx-auto space-y-4" data-testid={`edu-article-${selected.id}`}>
        <Button
          variant="ghost"
          onClick={() => setSelected(null)}
          className="pl-0 hover:pl-2 transition-all"
          data-testid="edu-back-btn"
        >
          <ArrowLeft size={16} weight="bold" className="mr-2" /> Volver a la biblioteca
        </Button>

        <div className="bg-card border border-border rounded-md p-6 md:p-10">
          <div className="flex items-center gap-2 mb-3">
            {selected.tags?.map((t) => (
              <Badge key={t} variant="outline" className="rounded-full">
                {t}
              </Badge>
            ))}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-3">
            {selected.title}
          </h1>
          {selected.summary && (
            <p className="text-base text-muted-foreground italic mb-6 leading-relaxed border-l-4 border-primary/30 pl-4">
              {selected.summary}
            </p>
          )}
          <div className="prose prose-sm max-w-none">{renderMarkdown(selected.body)}</div>
        </div>
      </article>
    );
  }

  return (
    <div className="space-y-6" data-testid="educacion-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-md bg-primary/10 grid place-items-center text-primary shrink-0">
          <BookOpen size={22} weight="duotone" />
        </div>
        <div>
          <div className="overline">Biblioteca</div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Educación Financiera</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Guías cortas y directas escritas por el equipo de Recobro para ayudarte a recuperar más cartera, comunicarte con clientes morosos y proteger tu negocio.
          </p>
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="bg-card border border-border rounded-md p-10 text-center" data-testid="edu-empty">
          <BookOpen size={40} weight="duotone" className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">Aún no hay guías publicadas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="edu-grid">
          {articles.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              data-testid={`edu-card-${a.id}`}
              className="text-left bg-card border border-border rounded-md p-5 hover:border-primary/40 hover:shadow-md transition-all flex flex-col gap-2 min-h-[160px]"
            >
              <div className="flex items-center gap-2">
                {a.tags?.slice(0, 2).map((t) => (
                  <Badge key={t} variant="outline" className="rounded-full text-[10px] uppercase tracking-wider">
                    {t}
                  </Badge>
                ))}
              </div>
              <h3 className="font-heading font-semibold text-lg leading-snug tracking-tight mt-1">
                {a.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {a.summary}
              </p>
              <span className="text-xs text-primary font-medium mt-auto">Leer guía →</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
